import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowUpRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TfAlertControls } from "@/components/trustedform/TfAlertControls";

const RANGES = [[24, "24 hours"], [168, "7 days"], [720, "30 days"]];
const seg = (on) => `px-2.5 py-1 text-[10px] uppercase tracking-widest border-r-2 border-black last:border-r-0 transition-colors duration-150 ${on ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`;

function ClaimChip({ on }) {
  return (
    <span className={`border px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold ${on ? "border-emerald-700 bg-emerald-50 text-emerald-800" : "border-amber-700 bg-amber-50 text-amber-800"}`}
          data-testid="tf-claim-chip">
      {on ? "Claiming on · certificates kept automatically" : "Claiming off · add your TrustedForm API key"}
    </span>
  );
}

function PageRow({ p, minLeads }) {
  const few = p.leads < minLeads;
  const low = p.below;
  return (
    <tr className="border-t border-zinc-200" data-testid={`tf-coverage-row-${p.slug}`}>
      <td className="px-3 py-2">
        <div className="font-bold text-xs">{p.title}</div>
        <div className="text-[10px] font-mono text-zinc-500">/lp/{p.slug}</div>
      </td>
      <td className="px-3 py-2 w-[38%]">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2.5 bg-zinc-100 border border-black/20">
            <div className={`h-full ${few ? "bg-zinc-400" : low ? "bg-red-600" : "bg-emerald-600"}`} style={{ width: `${p.pct}%` }} />
          </div>
          <span className={`font-mono text-xs font-bold w-12 text-right ${low ? "text-red-700" : ""}`} data-testid={`tf-coverage-pct-${p.slug}`}>{p.pct}%</span>
        </div>
        {few && <div className="text-[10px] text-zinc-500 mt-0.5" data-testid={`tf-coverage-few-${p.slug}`}>Too few leads to judge (under {minLeads})</div>}
      </td>
      <td className="px-3 py-2 font-mono text-xs">{p.with_cert} / {p.leads}</td>
      <td className="px-3 py-2 font-mono text-xs">{p.retained}</td>
      <td className={`px-3 py-2 font-mono text-xs ${p.mismatched ? "text-red-700 font-bold" : ""}`}>{p.mismatched}</td>
    </tr>
  );
}

function CoverageTable({ pages, minLeads }) {
  const TH = "px-3 py-2 text-[10px] uppercase tracking-widest text-left whitespace-nowrap";
  return (
    <div className="overflow-x-auto border-2 border-black bg-white">
      <table className="w-full min-w-[620px]" data-testid="tf-coverage-table">
        <thead className="bg-zinc-100">
          <tr><th className={TH}>Landing page</th><th className={TH}>With certificate</th><th className={TH}>Leads</th><th className={TH}>Claimed</th><th className={TH}>Doesn&apos;t match</th></tr>
        </thead>
        <tbody>
          {pages.map((p) => <PageRow key={p.slug} p={p} minLeads={minLeads} />)}
          {!pages.length && (
            <tr><td colSpan={5} className="px-3 py-6 text-center label-mono" data-testid="tf-coverage-empty">No landing page leads in this range yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export const TfCoverageCard = ({ compact = false }) => {
  const { user } = useAuth();
  const canEdit = ["super_admin", "admin"].includes(user?.role);
  const [hours, setHours] = useState(compact ? 168 : 24);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      setData((await api.get("/trustedform/coverage", { params: { hours } })).data);
      setError("");
    } catch (e) {
      setData(null);
      setError("Couldn't load certificate coverage. Try again in a moment.");
    }
  }, [hours]);
  useEffect(() => { load(); }, [load]);

  const t = data?.totals;
  const pages = compact ? (data?.pages || []).slice(0, 5) : (data?.pages || []);
  return (
    <div className="brut-card p-5 mb-6" data-testid={compact ? "dashboard-tf-coverage" : "tf-coverage-card"}>
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldCheck size={18} weight="bold" />
        <div className="font-chivo font-bold uppercase tracking-tight">TrustedForm certificate coverage</div>
        {data && <ClaimChip on={data.claim_enabled} />}
        <div className="ml-auto flex items-center gap-2">
          <div className="flex border-2 border-black">
            {RANGES.map(([h, l]) => (
              <button key={h} type="button" className={seg(hours === h)} onClick={() => setHours(h)} data-testid={`tf-coverage-range-${h}`}>{l}</button>
            ))}
          </div>
          {compact && (
            <Link to="/admin/landing-pages" className="text-[11px] uppercase tracking-widest border border-black px-2 py-1 hover:bg-black hover:text-white" data-testid="dashboard-tf-coverage-open">
              Details <ArrowUpRight className="inline" size={12} />
            </Link>
          )}
        </div>
      </div>
      {!data && !error && <div className="label-mono mt-3" data-testid="tf-coverage-loading">Loading…</div>}
      {error && <div className="mt-3 border-2 border-red-700 bg-red-50 p-3 text-xs" data-testid="tf-coverage-error">{error}</div>}
      {data && (
        <>
          <div className="flex gap-6 flex-wrap mt-3 mb-3">
            <div><div className="label-mono">Leads with a certificate</div><div className="font-chivo font-black text-3xl" data-testid="tf-coverage-total-pct">{t.pct == null ? "—" : `${t.pct}%`}</div></div>
            <div><div className="label-mono">Leads</div><div className="font-chivo font-black text-3xl" data-testid="tf-coverage-total-leads">{t.leads}</div></div>
            <div><div className="label-mono">Claimed</div><div className="font-chivo font-black text-3xl" data-testid="tf-coverage-total-retained">{t.retained}</div></div>
            <div><div className="label-mono">Doesn&apos;t match</div><div className={`font-chivo font-black text-3xl ${t.mismatched ? "text-red-700" : ""}`} data-testid="tf-coverage-total-mismatched">{t.mismatched}</div></div>
            <div><div className="label-mono">Pages below {data.alerts.threshold}%</div><div className={`font-chivo font-black text-3xl ${t.pages_below ? "text-red-700" : ""}`} data-testid="tf-coverage-pages-below">{t.pages_below}</div></div>
          </div>
          <CoverageTable pages={pages} minLeads={data.alerts.min_leads} />
          {!compact && <TfAlertControls alerts={data.alerts} canEdit={canEdit} onSaved={load} />}
        </>
      )}
    </div>
  );
};
